import { VendorMatchScore } from './types/job';
import { Award, Star, Briefcase, ShieldCheck } from 'lucide-react';

export interface CriterionBreakdownItem {
  icon: typeof Award;
  label: string;
  score: number;
  detail: string;
  met: boolean;
}

/**
 * Formats the vendor match score into a list of criterion breakdown items
 * for display in tooltips and popovers.
 * 
 * Scoring criteria (distance removed):
 * - Certifications: Proportional based on matched/required
 * - Rating: Proportional based on vendor rating vs minimum
 * - Experience: Proportional based on completed jobs vs minimum
 * - Verification: Binary based on verification level
 */
export function formatCriterionBreakdown(matchScore: VendorMatchScore): CriterionBreakdownItem[] {
  const items: CriterionBreakdownItem[] = [];
  const { details } = matchScore;

  if (!details) {
    console.warn('[formatCriterionBreakdown] No details found in matchScore!');
    return items;
  }
  
  if (!matchScore || typeof matchScore.score !== 'number') {
    console.error('[formatCriterionBreakdown] Invalid matchScore object:', matchScore);
    return items;
  }

  // Certifications criterion
  if (details.certifications_required && details.certifications_required.length > 0) {
    const score = Math.round(details.certifications_score || 0);
    const matchedCount = details.certifications_matched || 0;
    const requiredCount = details.certifications_required.length;
    
    items.push({
      icon: Award,
      label: 'Certifications',
      score,
      detail: `${matchedCount} of ${requiredCount} required`,
      met: details.certifications_met || false,
    });
  }

  // Rating criterion
  if (details.min_rating_required) {
    const score = Math.round(details.rating_score || 0);
    const detail = details.vendor_rating
      ? `${details.vendor_rating.toFixed(1)} stars (min ${details.min_rating_required})`
      : 'No rating yet';
    
    items.push({
      icon: Star,
      label: 'Rating',
      score,
      detail,
      met: details.rating_met || false,
    });
  }

  // Completed Jobs criterion
  if (details.min_jobs_required) {
    const score = Math.round(details.jobs_score || 0);
    const jobsCount = details.vendor_completed_jobs || 0;
    
    items.push({
      icon: Briefcase,
      label: 'Experience',
      score,
      detail: `${jobsCount} completed (min ${details.min_jobs_required})`,
      met: details.jobs_met || false,
    });
  }

  // Verification Level criterion
  if (details.min_verification_level && details.min_verification_level !== 'basic') {
    const score = Math.round(details.verification_score || 0);
    const vendorLevel = details.vendor_verification_level || 'basic';
    
    items.push({
      icon: ShieldCheck,
      label: 'Verification',
      score,
      detail: `${vendorLevel} (requires ${details.min_verification_level})`,
      met: details.verification_met || false,
    });
  }

  console.log('[formatCriterionBreakdown] Output items count:', items.length, 'items:', items);
  return items;
}

/**
 * Gets a color class based on score percentage
 */
export function getScoreColorClass(score: number): string {
  if (score >= 90) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  return 'text-red-600';
}
